import { useState, useEffect } from 'react'
import { message } from 'antd'

const usePsychologicalSupport = () => {
  const [loading, setLoading] = useState(false)
  const [resources, setResources] = useState([])

  useEffect(() => {
    const fetchResources = async () => {
      setLoading(true)
      try {
        // Simulate API call to fetch psychological support resources
        await new Promise((resolve) => setTimeout(resolve, 800))

        // Normally we would fetch from an API
        // For now, we'll use the resources from the local mock data
        let response = []
        try {
          const module = await import('../utils/psychologicalSupport.json')
          if (module && module.default) {
            response = module.default
          } else {
            console.error('Error loading psychologicalSupport.json: Module or default export not found')
            message.error('Failed to load psychological support resources data')
          }
        } catch (importError) {
          console.error('Error importing psychologicalSupport.json:', importError)
          message.error('Failed to load psychological support resources data')
        }

        setResources(response)
      } catch (error) {
        console.error('Error fetching psychological support resources:', error)
        message.error('Failed to load psychological support resources')
      } finally {
        setLoading(false)
      }
    }

    fetchResources()
  }, [])

  const addResource = async (resourceData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const newResource = {
        id: `psych-resource-${Date.now()}`,
        ...resourceData,
        completed: false
      }

      setResources([...resources, newResource])
      return newResource
    } catch (error) {
      console.error('Error adding psychological support resource:', error)
      message.error('Failed to add psychological support resource')
      return null
    } finally {
      setLoading(false)
    }
  }

  const updateResource = async (resourceId, resourceData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const updatedResources = resources.map((resource) => {
        if (resource.id === resourceId) {
          return { ...resource, ...resourceData }
        }
        return resource
      })

      setResources(updatedResources)
      return true
    } catch (error) {
      console.error('Error updating psychological support resource:', error)
      message.error('Failed to update psychological support resource')
      return false
    } finally {
      setLoading(false)
    }
  }

  const deleteResource = async (resourceId) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      const updatedResources = resources.filter((resource) => resource.id !== resourceId)
      setResources(updatedResources)
      return true
    } catch (error) {
      console.error('Error deleting psychological support resource:', error)
      message.error('Failed to delete psychological support resource')
      return false
    } finally {
      setLoading(false)
    }
  }

  const markResourceCompleted = async (resourceId, completionNotes) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 400))

      const updatedResources = resources.map((resource) => {
        if (resource.id === resourceId) {
          return {
            ...resource,
            completed: true,
            completionNotes,
            completedDate: new Date().toISOString()
          }
        }
        return resource
      })

      setResources(updatedResources)
      return true
    } catch (error) {
      console.error('Error marking resource as completed:', error)
      message.error('Failed to mark resource as completed')
      return false
    } finally {
      setLoading(false)
    }
  }

  const getResourcesByType = (type) => {
    return resources.filter((resource) => resource.type === type)
  }

  return {
    loading,
    resources,
    addResource,
    updateResource,
    deleteResource,
    markResourceCompleted,
    getResourcesByType
  }
}

export default usePsychologicalSupport
